import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiUser, FiThumbsUp, FiTrendingUp } = FiIcons;

function CustomerStories() {
  const testimonials = [
    {
      id: 1,
      name: 'Sarah MacLeod',
      location: 'Halifax, NS',
      rating: 5,
      text: "The AI assistant helped me find the perfect AWD SUV for Halifax winters. It understood exactly what I needed for Maritime driving conditions and saved me hours of searching. The whole process was seamless!",
      vehicle: '2023 Audi Q5 Quattro',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=80&h=80&fit=crop&crop=face'
    },
    {
      id: 2,
      name: 'Michael Chen',
      location: 'Dartmouth, NS',
      rating: 5,
      text: "Booked my winter tire installation through the AI chat at 11 PM. Got confirmation instantly and the service was exactly as promised. Revolutionary customer service for Halifax!",
      vehicle: 'Service Customer',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      location: 'Bedford, NS',
      rating: 5,
      text: "The AI trade-in estimator gave me a fair CAD value instantly, considering Maritime climate wear. No waiting, no pressure. When I came in, they honoured the estimate exactly. Honest Halifax dealership!",
      vehicle: '2022 BMW X3 xDrive',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face'
    }
  ];

  const stats = [
    { label: 'Halifax Customer Satisfaction', value: '98%', icon: FiThumbsUp },
    { label: 'AI Interactions Daily', value: '500+', icon: FiTrendingUp },
    { label: 'Average Response Time', value: '< 30s', icon: FiStar },
    { label: 'Recommend to Maritimers', value: '92%', icon: FiUser }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-white mb-4">
            Halifax Customer Stories
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See how our AI-powered experience is transforming the way Maritime customers buy and service their vehicles in Halifax.
          </p>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
        >
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={stat.icon} className="w-8 h-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
              <div className="text-gray-300 text-sm">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Testimonials */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20"
            >
              <div className="flex items-center space-x-4 mb-4">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-semibold text-white">{testimonial.name}</h4>
                  <p className="text-gray-300 text-sm">{testimonial.location}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-gray-200 mb-4 italic">
                "{testimonial.text}"
              </blockquote>
              <div className="text-blue-300 text-sm font-medium">
                {testimonial.vehicle}
              </div>
            </motion.div>
          ))}
        </div>

        {/* AI Sentiment Analysis */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-8 border border-blue-500/30"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Halifax AI Sentiment Analysis</h3>
            <p className="text-gray-300 mb-6">
              Our AI analyzes Halifax customer feedback in real-time to continuously improve your Maritime driving experience.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400">94%</div>
                <div className="text-gray-300">Positive Halifax Sentiment</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400">4.9/5</div>
                <div className="text-gray-300">Maritime Customer Rating</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-400">850+</div>
                <div className="text-gray-300">Halifax Reviews Analyzed</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default CustomerStories;